import React, { useEffect, useState } from "react";
import { BarChart3, Play, FileDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, canFinancial } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState, Loading } from "@/components/common";
import { Btn, Select, TextInput } from "@/components/DataTable";

const CATEGORIES = [
  { label: "Operational", reports: [
    ["jobs_completed", "Jobs Completed"], ["jobs_outstanding", "Jobs Outstanding"], ["overdue_jobs", "Overdue Jobs"],
    ["cancelled_jobs", "Cancelled Jobs"], ["unscheduled_work", "Unscheduled Work"] ] },
  { label: "Workload", reports: [["consultant_workload", "Consultant Workload"]] },
  { label: "Client & Contract", reports: [["contract_delivery", "Contract Delivery"]] },
  { label: "Financial", financial: true, reports: [
    ["revenue_by_client", "Revenue by Client"], ["revenue_by_month", "Revenue by Month"],
    ["gross_margin", "Gross Margin"], ["ready_for_invoicing", "Ready for Invoicing"] ] },
];

export default function Reports() {
  const { user } = useAuth();
  const fin = canFinancial(user);
  const canExport = can(user, "export_reports");
  const [key, setKey] = useState("jobs_completed");
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [filters, setFilters] = useState({ client_id: "", consultant_id: "", date_from: "", date_to: "" });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); api.get("/consultants").then((r) => setConsultants(r.data)); }, []);

  const cats = CATEGORIES.filter((c) => !c.financial || fin);

  const params = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p;
  };

  const run = async () => {
    setLoading(true); setReport(null);
    try { const { data } = await api.get(`/reports/${key}?${params()}`); setReport(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const download = async (fmt) => {
    try {
      const p = params(); p.set("fmt", fmt);
      const res = await api.get(`/reports/${key}/export?${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${key}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Reporting" subtitle="Operational, workload, client and commercial reporting with export" icon={BarChart3} />
      <div className="grid lg:grid-cols-4 gap-5">
        <div className="lg:col-span-1 space-y-4">
          <SectionCard title="Report" noPad>
            <div className="divide-y divide-slate-100">
              {cats.map((cat) => (
                <div key={cat.label} className="py-1">
                  <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">{cat.label}</div>
                  {cat.reports.map(([k, label]) => (
                    <button key={k} onClick={() => { setKey(k); setReport(null); }} data-testid={`report-${k}`}
                      className={`w-full text-left px-4 py-2 text-sm ${key === k ? "bg-seq-green/10 text-seq-green font-medium border-l-2 border-seq-green" : "text-slate-600 hover:bg-slate-50"}`}>{label}</button>
                  ))}
                </div>
              ))}
            </div>
          </SectionCard>
          <SectionCard title="Filters">
            <div className="space-y-3">
              <Select value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}><option value="">All clients</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}</Select>
              <Select value={filters.consultant_id} onChange={(e) => setFilters({ ...filters, consultant_id: e.target.value })}><option value="">All consultants</option>{consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
              <div className="grid grid-cols-2 gap-2">
                <TextInput type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
                <TextInput type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
              </div>
              <Btn onClick={run} className="w-full" data-testid="run-report-btn"><Play size={15} /> Run report</Btn>
            </div>
          </SectionCard>
        </div>

        <div className="lg:col-span-3">
          <SectionCard title={report ? report.title : "Results"} noPad
            action={report && canExport && (
              <div className="flex gap-1.5">
                <button onClick={() => download("csv")} data-testid="export-csv" className="text-xs flex items-center gap-1 px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"><FileDown size={13} /> CSV</button>
                <button onClick={() => download("xlsx")} data-testid="export-xlsx" className="text-xs flex items-center gap-1 px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"><FileSpreadsheet size={13} /> Excel</button>
                <button onClick={() => download("pdf")} data-testid="export-pdf" className="text-xs flex items-center gap-1 px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"><FileText size={13} /> PDF</button>
              </div>
            )}>
            {loading ? <Loading /> : !report ? <EmptyState icon={BarChart3} title="Run a report to see results" hint="Pick a report and filters on the left." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>{report.columns.map((c) => <th key={c} className="px-4 py-2 text-left font-medium">{c}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.rows.map((row, i) => (
                      <tr key={i} className={i % 2 ? "bg-slate-50/40" : ""}>
                        {row.map((cell, j) => <td key={j} className="px-4 py-2 text-slate-600">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.rows.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No data for the selected filters.</div>}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
